import { auth, firebaseConfig } from '../utils/firebase';

// Firebase Realtime Database REST API endpoint - get from config to handle different regions
const FIREBASE_DB_URL = firebaseConfig.databaseURL;

/**
 * Database service for Firebase Realtime Database via REST API
 */
class DatabaseService {
  constructor() {
    this.userId = null;
  }

  /**
   * Set the current user ID for Firebase operations
   */
  setUserId(userId) {
    this.userId = userId;
  }

  /**
   * Get auth token for Firebase REST API
   */
  async getAuthToken() {
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }
    return await auth.currentUser.getIdToken();
  }

  /**
   * Get the Firebase path for the data type
   */
  getKey(dataType) {
    if (!this.userId) {
      throw new Error('User ID not set for operations');
    }
    return `users/${this.userId}/${dataType}`;
  }

  /**
   * Save data to Firebase
   */
  async saveData(dataType, data) {
    const path = this.getKey(dataType);
    const token = await this.getAuthToken();
    const url = `${FIREBASE_DB_URL}/${path}.json?auth=${token}`;

    console.log(`Saving ${dataType} to Firebase at ${path}:`, data);

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to save ${dataType}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Load data from Firebase
   */
  async loadData(dataType) {
    const path = this.getKey(dataType);
    const token = await this.getAuthToken();
    const url = `${FIREBASE_DB_URL}/${path}.json?auth=${token}`;

    console.log(`Loading ${dataType} from Firebase at ${path}`);

    const response = await fetch(url);

    if (!response.ok) {
      // Don't throw error for 404 - it just means no data exists yet
      if (response.status === 404) {
        console.log(`No ${dataType} data found (404) - returning null`);
        return null;
      }
      throw new Error(`Failed to load ${dataType}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`Loaded ${dataType} from Firebase:`, data);

    if (!data) return null;

    // Convert Firebase objects back to arrays if needed
    if (this.isArrayType(dataType) && data && typeof data === 'object' && !Array.isArray(data)) {
      console.log(`Converting ${dataType} from object to array`);
      return Object.values(data);
    }

    return data;
  }

  /**
   * Check if a data type should be an array
   */
  isArrayType(dataType) {
    return ['portfolios', 'pots', 'goals', 'transactions', 'snapshots', 'fundPrices', 'proposedTransactions', 'schoolFeePlans'].includes(dataType);
  }

  /**
   * Clear all data (for testing/development)
   */
  async clearAllData() {
    const dataTypes = ['portfolios', 'pots', 'goals', 'transactions', 'snapshots', 'fundPrices', 'proposedTransactions', 'schoolFeePlans', 'exchangeRates', 'settings'];

    const token = await this.getAuthToken();
    const promises = dataTypes.map(async (type) => {
      const path = this.getKey(type);
      const url = `${FIREBASE_DB_URL}/${path}.json?auth=${token}`;
      const response = await fetch(url, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`Failed to delete ${type}: ${response.statusText}`);
      }
      return response.json();
    });

    return Promise.all(promises);
  }
}

// Export singleton instance
export const databaseService = new DatabaseService();
